import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import { notify } from 'helpers/helpers'

function Modifiers({
	showModifierModal,
	setShowModifierModal,
	showAddModifierModal,
	setShowAddModifierModal,
	addModifierToProduct,
	activeTab,
	attachedIds = [],
}) {
	const { t } = useTranslation()

	const [modifiers, setModifiers] = useState([])
	const [modifierProducts, setModifierProducts] = useState([])
	const [selectedModifier, setSelectedModifier] = useState({})
	const [groupSearch, setGroupSearch] = useState('')
	const [productSearch, setProductSearch] = useState('')
	const [addSearch, setAddSearch] = useState('')
	const [productModifiers, setProductModifiers] = useState({
		'parent': false,
		'modifiers': []
	})

	const filteredModifiers = useMemo(() => {
		const q = groupSearch.trim().toLowerCase()
		if (!q) return modifiers
		return modifiers.filter((item) => {
			if (String(item.name || '').toLowerCase().includes(q)) return true
			return (item.products || []).some((p) =>
				String(p.name || '').toLowerCase().includes(q)
			)
		})
	}, [modifiers, groupSearch])

	const filteredProducts = useMemo(() => {
		const q = productSearch.trim().toLowerCase()
		if (!q) return modifierProducts
		return modifierProducts.filter((item) =>
			String(item.name || '').toLowerCase().includes(q)
		)
	}, [modifierProducts, productSearch])

	const filteredAddModifiers = useMemo(() => {
		const q = addSearch.trim().toLowerCase()
		if (!q) return modifiers
		return modifiers.filter((item) => {
			if (String(item.name || '').toLowerCase().includes(q)) return true
			return (item.products || []).some((p) =>
				String(p.name || '').toLowerCase().includes(q)
			)
		})
	}, [modifiers, addSearch])

	async function createUpdate() {
		const name = String(selectedModifier.name || '').trim()
		if (!name) {
			notify(t('name2'))
			return
		}

		if (!selectedModifier.id) {
			await POST('/modifiers', { ...selectedModifier, name }, { loader: true })
			setSelectedModifier({})
		} else {
			await PUT('/modifiers', { ...selectedModifier, name }, { loader: true })
			setSelectedModifier({})
		}

		getModifiers()
	}

	async function addModifierToProductHOC() {
		addModifierToProduct(productModifiers)
		setProductModifiers({
			'parent': false,
			'modifiers': []
		})
	}

	// Группа уже стоит у блюда — второй раз её не привязать
	function isAttached(item) {
		return attachedIds.some((id) => Number(id) === Number(item.id))
	}

	// Клик по группе отмечает её, повторный клик снимает отметку
	function selectModifierToProduct(item) {
		if (isAttached(item)) return

		const selected = !!productModifiers?.modifiers?.find((element) => element.id === item.id)
		setProductModifiers({
			...productModifiers,
			'modifiers': selected
				? productModifiers.modifiers.filter((element) => element.id !== item.id)
				: [...(productModifiers.modifiers || []), item],
		})
	}

	async function selectProductToModifier(item) {
		if (!selectedModifier?.id) {
			notify(t('select_modifier_group'))
			return
		}
		var sendData = {
			'parent_id': selectedModifier.id,
			'product_id': item.id,
		}
		await POST('/modifiers-product', sendData, { loader: true })
		getModifiers()
	}

	async function deleteModifier(item, e) {
		if (e) {
			e.preventDefault()
			e.stopPropagation()
		}
		await DELETE('/modifiers', { 'id': item.id }, { loader: true })
		if (selectedModifier?.id === item.id) {
			setSelectedModifier({})
		}
		getModifiers()
	}

	function addModifierModal(bool) {
		if (!bool) {
			setShowAddModifierModal(false)
			setAddSearch('')
			setProductModifiers({
				'parent': false,
				'modifiers': []
			})
		}
	}

	function closeCreateModal() {
		setShowModifierModal(false)
		setSelectedModifier({})
		setGroupSearch('')
		setProductSearch('')
	}

	function existInSelected(item) {
		return !!productModifiers?.modifiers?.find((element) => element.id === item.id)
	}

	function isProductInSelectedGroup(item) {
		if (!selectedModifier?.id) return false
		const group = modifiers.find((m) => m.id === selectedModifier.id)
		return !!(group?.products || []).find((p) => Number(p.product_id) === Number(item.id))
	}

	async function getModifiers() {
		const response = await GET('/modifiers')
		if (httpOk(response)) {
			setModifiers(response.data)
			if (selectedModifier?.id) {
				const next = (response.data || []).find((m) => m.id === selectedModifier.id)
				if (next) setSelectedModifier(next)
			}
		}
	}

	async function getModifierProducts() {
		const response = await GET('/helper/product-modifiers')
		if (httpOk(response)) setModifierProducts(response.data)
	}

	useEffect(() => {
		if (activeTab === 2) {
			getModifiers()
			getModifierProducts()
		}
	}, [activeTab]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{/* MODAL CREATE / EDIT MODIFIER GROUPS */}
			<Modal
				show={showModifierModal}
				animation={false}
				centered
				size="xl"
				dialogClassName="modifiers-admin-modal"
				onHide={closeCreateModal}>
				<Modal.Header closeButton>
					<Modal.Title>{t('modifiers')}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="modifiers-admin-body">
					<div className="modifiers-admin-form">
						<div className="modifiers-admin-form-label">
							{selectedModifier.id ? t('edit') : t('create')} — {t('group')}
						</div>
						<div className="modifiers-admin-form-row">
							<div className="position-relative flex-grow-1">
								<input
									type="text"
									className="form-control"
									placeholder={t('name2')}
									value={selectedModifier.name ?? ''}
									onChange={(e) => setSelectedModifier({ ...selectedModifier, 'name': e.target.value })}
								/>
								{selectedModifier.id &&
									<button
										type="button"
										className="table-action-button table-action-danger-button modifiers-admin-clear"
										onClick={() => setSelectedModifier({})}>
										<i className="uil uil-times"></i>
									</button>
								}
							</div>
							{selectedModifier.id ? (
								<button
									type="button"
									className="btn btn-primary"
									disabled={!String(selectedModifier.name || '').trim()}
									onClick={createUpdate}>
									{t('edit')}
								</button>
							) : (
								<button
									type="button"
									className="btn btn-primary"
									disabled={!String(selectedModifier.name || '').trim()}
									onClick={createUpdate}>
									{t('create')}
								</button>
							)}
						</div>
						<div className="modifiers-admin-hint">
							{selectedModifier.id
								? t('modifier_hint_add_products')
								: t('modifier_hint_select_or_create')}
						</div>
					</div>

					<div className="modifiers-admin-panels">
						<div className="modifiers-admin-panel">
							<div className="modifiers-admin-panel-head">
								<div className="modifiers-admin-panel-title">{t('group')}</div>
								<input
									type="text"
									className="form-control form-control-sm"
									placeholder={t('search')}
									value={groupSearch}
									onChange={(e) => setGroupSearch(e.target.value)}
								/>
							</div>
							<div className="modifiers-admin-scroll">
								{filteredModifiers.length === 0 && (
									<div className="modifiers-admin-empty">{t('not_found')}</div>
								)}
								{filteredModifiers.map((item, index) => {
									const isActive = selectedModifier.id === item.id
									return (
										<div
											key={item.id ?? index}
											className={`modifiers-admin-group${isActive ? ' is-active' : ''}`}
											onClick={() => setSelectedModifier(item)}>
											<div className="modifiers-admin-group-row">
												<div className="modifiers-admin-group-name">
													<span className="modifiers-admin-group-index">{index + 1}.</span>
													<b>{item.name}</b>
													<span className="modifiers-admin-count">
														{(item.products || []).length}
													</span>
												</div>
												<button
													type="button"
													className="table-action-button table-action-danger-button"
													title={t('delete')}
													onClick={(e) => deleteModifier(item, e)}>
													<i className="uil uil-trash-alt"></i>
												</button>
											</div>
											{(item.products || []).length > 0 && (
												<div className="modifiers-admin-children">
													{item.products.map((product, productIndex) => (
														<div
															className="modifiers-admin-child"
															key={product.id ?? productIndex}>
															<span className="modifiers-admin-child-name">
																{product.name}
															</span>
															<button
																type="button"
																className="table-action-button table-action-danger-button"
																title={t('delete')}
																onClick={(e) => deleteModifier(product, e)}>
																<i className="uil uil-times"></i>
															</button>
														</div>
													))}
												</div>
											)}
										</div>
									)
								})}
							</div>
						</div>

						<div className="modifiers-admin-panel">
							<div className="modifiers-admin-panel-head">
								<div className="modifiers-admin-panel-title">
									{t('products')}
									{selectedModifier?.name ? (
										<span className="modifiers-admin-panel-sub">
											→ {selectedModifier.name}
										</span>
									) : null}
								</div>
								<input
									type="text"
									className="form-control form-control-sm"
									placeholder={t('search')}
									value={productSearch}
									onChange={(e) => setProductSearch(e.target.value)}
									disabled={!selectedModifier?.id}
								/>
							</div>
							<div className="modifiers-admin-scroll">
								{!selectedModifier?.id && (
									<div className="modifiers-admin-empty">
										{t('modifier_hint_select_group')}
									</div>
								)}
								{!!selectedModifier?.id && filteredProducts.length === 0 && (
									<div className="modifiers-admin-empty">{t('not_found')}</div>
								)}
								{!!selectedModifier?.id && filteredProducts.map((item, index) => {
									const alreadyIn = isProductInSelectedGroup(item)
									return (
										<div
											className={`modifiers-admin-product${alreadyIn ? ' is-added' : ''}`}
											key={item.id ?? index}>
											<span className="modifiers-admin-product-name">{item.name}</span>
											<button
												type="button"
												className="table-action-button table-action-primary-button"
												disabled={alreadyIn}
												title={alreadyIn ? t('added') : t('add')}
												onClick={() => selectProductToModifier(item)}>
												<i className={`uil ${alreadyIn ? 'uil-check' : 'uil-plus'}`}></i>
											</button>
										</div>
									)
								})}
							</div>
						</div>
					</div>
				</Modal.Body>
			</Modal>

			{/* MODAL ADD MODIFIER TO PRODUCT */}
			<Modal
				show={showAddModifierModal}
				animation={false}
				centered
				dialogClassName="modifiers-pick-modal"
				onHide={() => addModifierModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('modifiers')}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="modifiers-pick-body">
					<input
						type="text"
						className="form-control mb-2"
						placeholder={t('search')}
						value={addSearch}
						onChange={(e) => setAddSearch(e.target.value)}
					/>
					<div className="modifiers-pick-scroll">
						{filteredAddModifiers.length === 0 && (
									<div className="modifiers-admin-empty">{t('not_found')}</div>
						)}
						{filteredAddModifiers.map((item, index) => {
							const attached = isAttached(item)
							const selected = attached || existInSelected(item)
							return (
								<button
									type="button"
									key={item.id ?? index}
									className={`modifiers-pick-item${selected ? ' is-selected' : ''}`}
									disabled={attached}
									title={attached ? 'Группа уже добавлена этому блюду' : undefined}
									onClick={() => selectModifierToProduct(item)}>
									<div className="modifiers-pick-item-main">
										<b>{index + 1}. {item.name}</b>
										{(item.products || []).length > 0 && (
											<div className="modifiers-pick-item-children">
												{(item.products || []).map((product) => product.name).join(', ')}
											</div>
										)}
									</div>
									<span className={`modifiers-pick-check${selected ? ' is-on' : ''}`}>
										<i className={`uil ${selected ? 'uil-check' : 'uil-plus'}`}></i>
									</span>
								</button>
							)
						})}
					</div>
					<button
						type="button"
						className="btn btn-primary w-100 mt-3"
						disabled={!productModifiers?.modifiers?.length}
						onClick={() => addModifierToProductHOC()}>
						{t('save')}
						{productModifiers?.modifiers?.length
							? ` (${productModifiers.modifiers.length})`
							: ''}
					</button>
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Modifiers
